/**
 * MapStatusBanners — estados de carregamento/erro e avisos de camada do M2 Mapa
 * (extraído do `index.tsx` em GUPM-205; sem mudança de comportamento).
 *
 * - `isLoading` → texto de carregamento (e nada mais).
 * - `error` → card de erro com a mensagem (e nada mais).
 * - caso ok: até 3 avisos `role="status"` (mancha / PAEBM / aguardando-coords),
 *   já pré-computados pelo container (cada um já considera os toggles de camada).
 */
import { FalhaDeCarga } from '@/components/FalhaDeCarga';

interface MapStatusBannersProps {
  isLoading: boolean;
  error: Error | null;
  /** E-mail do suporte (GUPM-336) — lido do banco pelo container; sem ele, a tela aponta a aba de contatos. */
  emailSuporte?: string | null;
  /** `layers.mancha && !!manchaError` */
  showMancha: boolean;
  /** `(layers.edificacoes || layers.pontosEncontro) && !!paebmError` */
  showPaebm: boolean;
  /**
   * Códigos dos instrumentos ativos sem pino na barragem selecionada (GUPM-227).
   * Vazio = nada a avisar. Substituiu o booleano `showAguardando`, que só cobria
   * o caso extremo de zero pinos e por isso ficava calado no caso real (parcial).
   */
  semPino: readonly string[];
}

export default function MapStatusBanners({
  isLoading,
  error,
  emailSuporte = null,
  showMancha,
  showPaebm,
  semPino,
}: MapStatusBannersProps) {
  if (isLoading) {
    return <p className="text-jf-muted text-sm">Carregando mapa…</p>;
  }

  if (error) {
    // GUPM-336: a dica de `supabase start` só aparece em DEV.
    return <FalhaDeCarga titulo="Erro ao carregar instrumentos" erro={error} emailSuporte={emailSuporte} />;
  }

  return (
    <>
      {showMancha && (
        <div
          role="status"
          className="mb-2 text-xs text-level-atencao rounded-lg border border-level-atencao/40 bg-level-atencao/10 px-3 py-2"
        >
          Não foi possível carregar a mancha de inundação (ZAS). O mapa segue
          funcional sem essa camada.
        </div>
      )}

      {/* GUPM-195: feedback quando a leitura das camadas PAEBM (banco) falha —
          senão o operador veria o mapa sem edificações/PEs, indistinguível de "sem dado". */}
      {showPaebm && (
        <div
          role="status"
          className="mb-2 text-xs text-level-atencao rounded-lg border border-level-atencao/40 bg-level-atencao/10 px-3 py-2"
        >
          Não foi possível carregar as edificações ZAS / pontos de encontro (PAEBM).
          O mapa segue funcional sem essas camadas.
        </div>
      )}

      {/* GUPM-227: nomeia quem ficou sem pino — sem os códigos aqui, o instrumento
          aparece na lista, não aparece no mapa, e nada explica por quê.

          GUPM-329: o texto dizia "o inventário GPX v2 não informa coordenada válida".
          Era exato enquanto todo instrumento vinha do GPX. Os marcos superficiais vêm
          da planilha de topografia da Galvani, e o `MS-2` da Mina entra nesta lista
          porque a planilha o traz zerado — nomear o GPX mandaria o RT cobrar
          coordenada de um fornecedor que não tem o instrumento. O banner passa a
          afirmar o FATO (não há coordenada), que vale para qualquer origem.

          O motivo por instrumento fica no relatório da carga de cada fonte:
          docs/reference/gpx-api/excecoes-inventario-v2.md para o GPX,
          docs/reference/marcos-superficiais/README.md para os marcos. */}
      {semPino.length > 0 && (
        <div
          role="status"
          className="mb-2 text-xs text-jf-muted rounded-lg border border-jf-border bg-jf-surface/60 px-3 py-2"
        >
          {`Sem pino no mapa — ${semPino.length === 1 ? 'este instrumento não tem' : 'estes instrumentos não têm'} coordenada válida cadastrada: ${semPino.join(', ')}. ${semPino.length === 1 ? 'Ele segue' : 'Eles seguem'} na lista abaixo, com leitura e nível.`}
        </div>
      )}
    </>
  );
}
